/**
 * 월별 팀 목표·인건비 합계 파생 정본 (2026-08-04 추출, 2026-09-02 이월 분리)
 *
 * 급여 저장 경로가 인건비합계·순수목표는 재계산하면서 최종목표만 메모리 값을 통과시켜
 * '새 인건비 + 옛 목표'가 영속되던 결함이 있었다. 저장 경로가 늘어날 때마다 같은 실수가
 * 반복되지 않도록, 세 값을 반드시 한 번에·같은 로스터에서 파생하는 단일 함수로 모은다.
 *
 * 최종목표 = 순수목표 + 이월금액(teamAdjustmentCarryover). 이 식 외의 규칙은 없다.
 * 이전에는 이월분 유실을 막으려고 Math.max(파생, 저장값)로 목표를 '내리지 않았'는데,
 * 그 결과 직원이 빠지거나 조정을 해제해도 목표가 내려가지 않았다(실측: 2026-09 1팀 +81,543).
 * 이월금액이 별도로 저장되면 유실될 것이 없으므로 보존 장치도 필요 없다.
 *
 * 전체 저장(internalSaveAllData)·제한 저장(실장)·월 전환 저장 모두 이 함수를 거쳐야 한다.
 */
import {
  calculateTeamSalaryTotal,
  resolveFinalTargetSales,
  getTeamAdjustmentCarryover,
} from './payrollCalculations.ts';

export interface MonthTargets {
  teamSalaryTotals: Record<string, number>;
  teamPureTargetSales: Record<string, number>;
  teamTargetSales: Record<string, number>;
  /** 팀별 이월금액 — 목표와 반드시 함께 저장되어 불변식(목표=순수+이월)이 DB에서도 성립하게 한다 */
  teamAdjustmentCarryover: Record<string, number>;
  /** 팀별 최종목표의 합 — 스칼라 targetSales와 팀맵이 어긋나지 않게 함께 파생한다 */
  totalTargetSales: number;
}

export interface DeriveMonthTargetsInput {
  teams: readonly string[];
  employees: readonly any[];
  /** 팀별 유효 인건비율(%)을 돌려주는 함수 — 폴백 체인은 호출부의 정본을 그대로 쓴다 */
  getPersonnelPercentage: (teamName: string) => number;
  /** 이월금액을 읽기 위한 예산 데이터 (monthlyData[month].teamAdjustmentCarryover) */
  budgetData: any;
  month: string;
  /** @deprecated 더 이상 파생에 쓰지 않는다 — 호출부 호환을 위해 남겨 둔 인자 */
  storedTeamTargetSales?: Record<string, any> | null;
}

/**
 * 인건비합계 → 순수목표 → 최종목표를 한 루프에서 파생한다.
 *
 * - 최종목표 = 순수목표 + 저장된 이월금액. 조정을 '새로' 적용하지 않는다(적용은 명시적 행동 소관).
 * - 인건비율 미설정(순수목표 0)이어도 예외 없이 0 + 이월. 저장값 보존은 불변식 위반 경로였다.
 */
export function deriveMonthTargets(input: DeriveMonthTargetsInput): MonthTargets {
  const { teams, employees, getPersonnelPercentage, budgetData, month } = input;
  const monthData = budgetData?.monthlyData?.[month];

  const teamSalaryTotals: Record<string, number> = {};
  const teamPureTargetSales: Record<string, number> = {};
  const teamTargetSales: Record<string, number> = {};
  const teamAdjustmentCarryover: Record<string, number> = {};

  teams.forEach(teamName => {
    if (!teamName) return;
    // 🔒 실장 배분 정본: 실장을 1회만 계상
    const salaryTotal = calculateTeamSalaryTotal(employees as any[], teamName, employees as any[]);
    teamSalaryTotals[teamName] = salaryTotal;

    const pct = Number(getPersonnelPercentage(teamName)) || 0;
    const pure = pct > 0 ? Math.round(salaryTotal / (pct / 100)) : 0;
    teamPureTargetSales[teamName] = pure;

    // 이월금액은 읽어서 그대로 다시 기록한다 — 목표와 같은 저장에 실려야 불변식이 DB에서 성립한다
    teamAdjustmentCarryover[teamName] = getTeamAdjustmentCarryover(monthData, teamName);

    // 불변식에 예외를 두지 않는다. 인건비율 미설정(순수 0)이어도 목표 = 0 + 이월.
    // 이전에는 '0 clobber 방지'로 저장값을 보존했는데, 그 보존이 곧 "목표 ≠ 순수 + 이월"인
    // 행을 DB에 남기는 경로였다(Codex 재심 P0-3). 운영 DB에는 순수 0인데 목표>0인 팀이 없다.
    teamTargetSales[teamName] = resolveFinalTargetSales(budgetData, month, teamName, pure);
  });

  const totalTargetSales = Object.values(teamTargetSales).reduce((sum, v) => sum + (Number(v) || 0), 0);
  return { teamSalaryTotals, teamPureTargetSales, teamTargetSales, teamAdjustmentCarryover, totalTargetSales };
}
